import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STEPS = ["New", "Contacted", "Shortlisted"];

export default function TrackEnquiry() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/track/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 data-testid="track-not-found" className="text-3xl font-extrabold text-slate-900 font-display">Link not found</h1>
        <p className="mt-3 text-slate-600">This tracking link is invalid or has expired.</p>
        <Link to="/enquiry"><Button className="mt-6 rounded-full">Submit a new enquiry</Button></Link>
      </div>
    );
  }
  if (!data) return <div className="py-24 text-center text-slate-500">Loading…</div>;

  const rejected = data.status === "Rejected";
  const activeIdx = STEPS.indexOf(data.status);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Application Tracker</p>
      <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-slate-900 font-display">Hi {data.full_name.split(" ")[0]},</h1>
      <p className="mt-2 text-slate-600">Here's the live status of your enquiry for <span className="font-semibold text-slate-900">{data.preferred_role}</span>.</p>

      <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6 lg:p-8">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-500">Current status</span>
          <span data-testid="track-status" className={`px-3 py-1 rounded-full text-sm font-semibold ${rejected ? "bg-rose-50 text-rose-700" : "bg-indigo-50 text-indigo-700"}`}>{data.status}</span>
        </div>

        {rejected ? (
          <p data-testid="track-rejected" className="mt-6 text-slate-700 leading-relaxed">Thank you for your interest. Unfortunately we couldn't take your profile forward this time. We encourage you to apply for other roles.</p>
        ) : (
          <ol className="mt-8 space-y-6">
            {STEPS.map((s, i) => {
              const done = i <= activeIdx;
              return (
                <li key={s} data-testid={`track-step-${s.toLowerCase()}`} className="flex items-start gap-4">
                  <span className={`mt-0.5 inline-flex w-8 h-8 rounded-full items-center justify-center shrink-0 ${done ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                    {done ? <Check size={16} /> : <Clock size={15} />}
                  </span>
                  <div>
                    <p className={`font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>{s === "New" ? "Enquiry received" : s}</p>
                    <p className="text-sm text-slate-500">{STEP_TEXT[s]}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <p className="mt-8 text-xs text-slate-400">Submitted {new Date(data.created_at).toLocaleString()}{data.updated_at ? ` · Updated ${new Date(data.updated_at).toLocaleString()}` : ""}</p>
      </div>
    </div>
  );
}

const STEP_TEXT = {
  New: "Our team has received your profile and resume.",
  Contacted: "A career mentor has reached out to discuss opportunities.",
  Shortlisted: "You've been shortlisted for matching roles.",
};
